import { useState, useEffect, ReactNode } from 'react';
import { CodeEditor } from '@/components/editor/CodeEditor';
import { FileTree } from '@/components/editor/FileTree';
import { Terminal } from '@/components/terminal/Terminal';
import { Button } from '@/components/ui/Button';
import { Play, RefreshCw } from 'lucide-react';

function renderInline(text: string): ReactNode[] {
  return text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return <code key={i} className="bg-dark-100 text-success-400 px-1 rounded text-xs font-mono">{part.slice(1, -1)}</code>;
    }
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={i} className="text-primary-300 font-semibold">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function MarkdownPreview({ content }: { content: string }) {
  const lines = content.split('\n');
  const elements: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="text-base font-bold text-primary-300 mt-4 mb-1">{line.slice(4)}</h3>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-lg font-bold text-primary-400 mt-5 mb-2 border-b border-dark-200 pb-1">{line.slice(3)}</h2>);
    } else if (line.startsWith('# ')) {
      elements.push(<h1 key={i} className="text-xl font-bold text-primary-400 mt-4 mb-3">{line.slice(2)}</h1>);
    } else if (line.startsWith('- ')) {
      // Collect consecutive list items
      const listItems: ReactNode[] = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        listItems.push(<li key={i} className="text-primary-200 text-sm">{renderInline(lines[i].slice(2))}</li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="list-disc list-inside space-y-1 my-2 ml-2">{listItems}</ul>);
      continue;
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(<p key={i} className="text-primary-200 text-sm leading-relaxed">{renderInline(line)}</p>);
    }
    i++;
  }

  return (
    <div className="rounded-lg overflow-hidden border border-dark-200 shadow-[0_0_20px_rgba(34,197,94,0.1)] bg-black">
      <div className="bg-dark-50 border-b border-dark-200 px-4 py-2 flex items-center gap-2">
        <span className="text-sm font-semibold text-primary-400">Preview</span>
        <span className="text-xs text-primary-500 ml-1">(Markdown)</span>
      </div>
      <div className="p-4 overflow-y-auto font-sans" style={{ height: '400px' }}>
        {elements}
      </div>
    </div>
  );
}

interface FileNode {
  name: string;
  type: 'file' | 'directory';
  path: string;
  content?: string;
  children?: FileNode[];
}

interface LevelPlaygroundProps {
  starterCode: Record<string, any>;
  onAction?: (action: string, data: any) => void;
}

export function LevelPlayground({ starterCode, onAction }: LevelPlaygroundProps) {
  const [files, setFiles] = useState<FileNode[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContents, setFileContents] = useState<Record<string, string>>({});
  const [currentDir, setCurrentDir] = useState<string>('/');
  const [terminalHistory, setTerminalHistory] = useState<string[]>([
    '$ Welcome to CodeQuest Terminal',
    '$ Type commands to interact with Claude Code tools',
    '$ Use "cd <directory>" to navigate',
    '',
  ]);

  // Get all unique directories from file paths
  const getDirectories = (): string[] => {
    const dirs = new Set<string>(['/']);
    Object.keys(fileContents).forEach((path) => {
      const parts = path.split('/');
      for (let i = 1; i < parts.length; i++) {
        const dir = '/' + parts.slice(0, i).join('/');
        dirs.add(dir);
      }
    });
    return Array.from(dirs).sort();
  };

  // Initialize files from starter code
  useEffect(() => {
    const fileTree: FileNode[] = [];
    const contents: Record<string, string> = {};

    Object.entries(starterCode).forEach(([path, data]: [string, any]) => {
      if (data.type === 'file') {
        contents[path] = data.content || '';

        // Build file tree structure
        const parts = path.split('/');
        const fileName = parts[parts.length - 1];

        if (parts.length === 1) {
          // Root level file
          fileTree.push({
            name: fileName,
            type: 'file',
            path,
          });
        } else {
          // Nested file - create directory structure
          const dirName = parts[0];
          let dir = fileTree.find(
            (n) => n.name === dirName && n.type === 'directory'
          );

          if (!dir) {
            dir = {
              name: dirName,
              type: 'directory',
              path: dirName,
              children: [],
            };
            fileTree.push(dir);
          }

          dir.children!.push({
            name: fileName,
            type: 'file',
            path,
          });
        }
      }
    });

    setFiles(fileTree);
    setFileContents(contents);

    // Select first file by default
    if (fileTree.length > 0) {
      const firstFile =
        fileTree[0].type === 'file'
          ? fileTree[0].path
          : fileTree[0].children?.[0]?.path;
      if (firstFile) setSelectedFile(firstFile);
    }
  }, [starterCode]);

  const handleFileSelect = (path: string) => {
    setSelectedFile(path);
    onAction?.('file_opened', { path });
  };

  const handleCodeChange = (value: string | undefined) => {
    if (selectedFile && value !== undefined) {
      setFileContents((prev) => ({
        ...prev,
        [selectedFile]: value,
      }));
      // Notify parent about file edit for validation
      onAction?.('file_edit', { file: selectedFile, content: value });
    }
  };

  // Resolve relative path based on current directory
  const resolvePath = (path: string): string => {
    if (path.startsWith('/')) return path.substring(1);
    if (currentDir === '/') return path;
    return currentDir.substring(1) + '/' + path;
  };

  const handleCommand = (command: string) => {
    const trimmedCommand = command.trim();
    const prompt = currentDir === '/' ? '$' : `${currentDir} $`;
    setTerminalHistory((prev) => [...prev, `${prompt} ${trimmedCommand}`, '']);

    // Handle cd command
    if (trimmedCommand.startsWith('cd')) {
      const parts = trimmedCommand.split(' ');
      const targetDir = parts[1];

      if (!targetDir) {
        setCurrentDir('/');
        setTerminalHistory((prev) => [...prev, `✓ Changed to /`, '']);
        return;
      }

      if (targetDir === '..') {
        // Go up one directory
        if (currentDir === '/') {
          setTerminalHistory((prev) => [...prev, `✗ Already at root directory`, '']);
        } else {
          const parts = currentDir.split('/').filter(Boolean);
          const newDir = parts.length > 1 ? '/' + parts.slice(0, -1).join('/') : '/';
          setCurrentDir(newDir);
          setTerminalHistory((prev) => [...prev, `✓ Changed to ${newDir}`, '']);
        }
        return;
      }

      if (targetDir === '/') {
        setCurrentDir('/');
        setTerminalHistory((prev) => [...prev, `✓ Changed to /`, '']);
        return;
      }

      // Check if target directory exists
      const allDirs = getDirectories();
      const resolvedDir = targetDir.startsWith('/')
        ? targetDir
        : currentDir === '/'
        ? '/' + targetDir
        : currentDir + '/' + targetDir;

      if (allDirs.includes(resolvedDir)) {
        setCurrentDir(resolvedDir);
        setTerminalHistory((prev) => [...prev, `✓ Changed to ${resolvedDir}`, '']);
      } else {
        setTerminalHistory((prev) => [
          ...prev,
          `✗ Error: Directory '${targetDir}' not found`,
          `  Available directories: ${allDirs.filter(d => d !== '/').map(d => d.substring(1)).join(', ')}`,
          '',
        ]);
      }
      return;
    }

    // Handle pwd command
    if (trimmedCommand === 'pwd') {
      setTerminalHistory((prev) => [...prev, currentDir, '']);
      return;
    }

    // Handle ls command
    if (trimmedCommand === 'ls' || trimmedCommand.startsWith('ls ')) {
      const targetPath = trimmedCommand.split(' ')[1];
      const searchDir = targetPath ? resolvePath(targetPath) : currentDir.substring(1);

      const filesInDir = Object.keys(fileContents).filter((path) => {
        if (searchDir === '/') return !path.includes('/');
        return path.startsWith(searchDir + '/') &&
               path.substring(searchDir.length + 1).split('/').length === 1;
      });

      const dirsInCurrentPath = new Set<string>();
      Object.keys(fileContents).forEach((path) => {
        if (searchDir === '/') {
          const firstPart = path.split('/')[0];
          if (path.includes('/')) dirsInCurrentPath.add(firstPart);
        } else if (path.startsWith(searchDir + '/')) {
          const remaining = path.substring(searchDir.length + 1);
          const firstDir = remaining.split('/')[0];
          if (remaining.includes('/')) dirsInCurrentPath.add(firstDir);
        }
      });

      const items = [
        ...Array.from(dirsInCurrentPath).map(d => `📁 ${d}/`),
        ...filesInDir.map(f => `📄 ${f.split('/').pop()}`)
      ];

      if (items.length === 0) {
        setTerminalHistory((prev) => [...prev, `  (empty directory)`, '']);
      } else {
        setTerminalHistory((prev) => [
          ...prev,
          ...items.map(item => `  ${item}`),
          '',
        ]);
      }
      return;
    }

    // Simulate read command
    if (trimmedCommand.startsWith('read')) {
      const fileName = trimmedCommand.split(' ')[1];
      const resolvedPath = resolvePath(fileName);

      if (fileName && fileContents[resolvedPath]) {
        setTerminalHistory((prev) => [
          ...prev,
          `Reading ${resolvedPath}...`,
          `✓ File loaded successfully`,
          '',
        ]);
        setSelectedFile(resolvedPath);
        onAction?.('read', { file: resolvedPath });
      } else {
        setTerminalHistory((prev) => [
          ...prev,
          `✗ Error: File '${fileName}' not found in ${currentDir}`,
          '',
        ]);
      }
      return;
    }

    // Simulate glob command
    if (trimmedCommand.startsWith('glob')) {
      const pattern = trimmedCommand.split(' ')[1] || '**/*';
      const matchedFiles = Object.keys(fileContents).filter((path) => {
        // Filter by current directory first
        const inCurrentDir = currentDir === '/' || path.startsWith(currentDir.substring(1) + '/') || (currentDir === '/' && !path.includes('/'));
        if (!inCurrentDir) return false;

        if (pattern === '**/*') return true;
        if (pattern.endsWith('*')) {
          const prefix = pattern.slice(0, -1);
          return path.includes(prefix);
        }
        return path.includes(pattern);
      });

      setTerminalHistory((prev) => [
        ...prev,
        `Searching for '${pattern}' in ${currentDir}...`,
        `✓ Found ${matchedFiles.length} file(s):`,
        ...matchedFiles.map((f) => `  - ${f}`),
        '',
      ]);
      onAction?.('glob', { pattern, results: matchedFiles });
      return;
    }

    // Simulate grep command
    if (trimmedCommand.startsWith('grep')) {
      const searchTerm = trimmedCommand.split(' ')[1];
      const results: string[] = [];

      Object.entries(fileContents).forEach(([path, content]) => {
        // Filter by current directory
        const inCurrentDir = currentDir === '/' || path.startsWith(currentDir.substring(1) + '/') || (currentDir === '/' && !path.includes('/'));

        if (inCurrentDir && content.includes(searchTerm)) {
          results.push(path);
        }
      });

      setTerminalHistory((prev) => [
        ...prev,
        `Searching for '${searchTerm}' in ${currentDir}...`,
        `✓ Found in ${results.length} file(s):`,
        ...results.map((f) => `  - ${f}`),
        '',
      ]);
      onAction?.('grep', { pattern: searchTerm, results });
      return;
    }

    // Clear command
    if (trimmedCommand === 'clear') {
      setTerminalHistory([
        '$ Welcome to CodeQuest Terminal',
        '$ Type commands to interact with Claude Code tools',
        '$ Use "cd <directory>" to navigate',
        '',
      ]);
      return;
    }

    // Help command
    if (trimmedCommand === 'help') {
      setTerminalHistory((prev) => [
        ...prev,
        'Available commands:',
        '  cd <dir>       - Change directory (use .. to go up)',
        '  pwd            - Print current directory',
        '  ls             - List files in current directory',
        '  read <file>    - Read a file',
        '  glob <pattern> - Find files by pattern',
        '  grep <text>    - Search for text in files',
        '  clear          - Clear terminal screen',
        '  help           - Show this help',
        '',
      ]);
      return;
    }

    // Unknown command
    setTerminalHistory((prev) => [
      ...prev,
      `✗ Unknown command: ${trimmedCommand}`,
      `Type 'help' for available commands`,
      '',
    ]);
  };

  const handleReset = () => {
    // Reset to starter code
    const contents: Record<string, string> = {};
    Object.entries(starterCode).forEach(([path, data]: [string, any]) => {
      if (data.type === 'file') {
        contents[path] = data.content || '';
      }
    });
    setFileContents(contents);
    setCurrentDir('/');
    setTerminalHistory([
      '$ Welcome to CodeQuest Terminal',
      '$ Type commands to interact with Claude Code tools',
      '$ Use "cd <directory>" to navigate',
      '',
    ]);
  };

  const currentFileContent = selectedFile ? fileContents[selectedFile] || '' : '';

  return (
    <div className="grid grid-cols-12 gap-4 h-[700px]">
      {/* File Tree Sidebar */}
      <div className="col-span-3 overflow-y-auto">
        <div className="mb-2 px-2 py-1 bg-dark-100 rounded border border-dark-200">
          <div className="text-xs text-primary-400 font-semibold">Current Directory:</div>
          <div className="text-sm text-success-400 font-mono">{currentDir}</div>
        </div>
        <FileTree
          files={files}
          onFileSelect={handleFileSelect}
          selectedFile={selectedFile || undefined}
        />
      </div>

      {/* Editor and Terminal */}
      <div className="col-span-9 flex flex-col gap-4">
        {/* Editor */}
        <div className="flex-1">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-semibold text-primary-400">
              {selectedFile || 'Select a file'}
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="flex items-center gap-2"
              >
                <RefreshCw size={14} />
                Reset
              </Button>
            </div>
          </div>
          {selectedFile?.endsWith('.md') ? (
            <MarkdownPreview content={currentFileContent} />
          ) : (
            <CodeEditor
              value={currentFileContent}
              onChange={handleCodeChange}
              language={
                selectedFile?.endsWith('.js')
                  ? 'javascript'
                  : selectedFile?.endsWith('.jsx')
                  ? 'javascript'
                  : selectedFile?.endsWith('.ts')
                  ? 'typescript'
                  : selectedFile?.endsWith('.tsx')
                  ? 'typescript'
                  : selectedFile?.endsWith('.py')
                  ? 'python'
                  : selectedFile?.endsWith('.json')
                  ? 'json'
                  : selectedFile?.endsWith('.css')
                  ? 'css'
                  : 'plaintext'
              }
              height="400px"
            />
          )}
        </div>

        {/* Terminal */}
        <div>
          <Terminal
            history={terminalHistory}
            onCommand={handleCommand}
            height="200px"
          />
        </div>
      </div>
    </div>
  );
}
